import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { adminApi } from "../../../lib/admin";
import type {
  AdminGigListItemResponse,
  AdminGigRequestMessageSummaryResponse,
  AdminGigRequestSummaryResponse,
  AdminReviewSummaryResponse,
} from "../../../types/adminTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatDateTime, formatDateTimeInGigTimeZone, formatEstimatedDuration } from "../../../utils/DateUtils";
import { formatGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";

type AdminGigDetailModalProps = {
  gig: AdminGigListItemResponse | null;
  isOpen: boolean;
  onClose: () => void;
};

const getRequesterDisplayName = (request: AdminGigRequestSummaryResponse) => {
  const fullName = `${request.requesterFirstName ?? ""} ${request.requesterLastName ?? ""}`.trim();
  return fullName || "Unknown requester";
};

const getRequestTone = (status: AdminGigRequestSummaryResponse["status"]) => {
  if (status === "POSTER_ACCEPTED") {
    return "success";
  }
  if (status === "POSTER_REJECTED" || status === "GUARDIAN_REJECTED" || status === "WITHDRAWN" || status === "CANCELLED") {
    return "danger";
  }
  if (status === "POSTER_PENDING" || status === "GUARDIAN_PENDING" || status === "PAYOUT_SETUP_PENDING" || status === "FUNDING_PENDING") {
    return "warning";
  }
  return "neutral";
};

const getFullName = (firstName?: string | null, lastName?: string | null, fallback = "Unknown user") => {
  const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return fullName || fallback;
};

const getCompletionItems = (
  request: AdminGigRequestSummaryResponse,
  posterName: string,
) => {
  const completionItems: Array<{ label: string; value: string }> = [];
  if (request.teenMarkedCompletedAt) {
    completionItems.push({
      label: `${getRequesterDisplayName(request)} marked complete`,
      value: formatDateTime(request.teenMarkedCompletedAt),
    });
  }
  if (request.adultConfirmedCompletedAt) {
    completionItems.push({
      label: `${posterName} confirmed complete`,
      value: formatDateTime(request.adultConfirmedCompletedAt),
    });
  }
  return completionItems;
};

const getMessageSenderLabel = (message: AdminGigRequestMessageSummaryResponse) => {
  if (message.senderRole === "SYSTEM") {
    return "MyTeenGig";
  }
  const roleLabel = message.senderRole === "ADULT" ? "Poster" : "Teen";
  return `${getFullName(message.senderFirstName, message.senderLastName)} (${roleLabel})`;
};

const getReviewContextLabel = (reviewContext: AdminReviewSummaryResponse["reviewContext"]) => (
  reviewContext === "WORKER_PERFORMANCE" ? "Worker performance" : "Poster experience"
);

const getReviewSummaryKey = (review: AdminReviewSummaryResponse, index: number) => [
  review.createdAt,
  review.gigTitle ?? "gig",
  review.reviewerEmail ?? getFullName(review.reviewerFirstName, review.reviewerLastName),
  review.revieweeEmail ?? getFullName(review.revieweeFirstName, review.revieweeLastName),
  review.reviewContext,
  review.rating,
  index,
].join("|");

type ModerationAction = "block" | "unblock";

const MAX_MODERATION_REASON_LENGTH = 500;

const AdminGigDetailModal = ({ gig, isOpen, onClose }: AdminGigDetailModalProps) => {
  const queryClient = useQueryClient();
  const [moderationAction, setModerationAction] = useState<ModerationAction | null>(null);
  const [moderationReason, setModerationReason] = useState("");
  const [moderationError, setModerationError] = useState<string | null>(null);

  const gigDetailQuery = useQuery({
    queryKey: ["admin-gig-detail", gig?.id],
    queryFn: () => adminApi.getGigDetail(gig!.id),
    enabled: isOpen && gig !== null,
  });

  const detail = gigDetailQuery.data;
  const posterName = detail
    ? getFullName(detail.posterFirstName, detail.posterLastName, "Unknown poster")
    : "Unknown poster";

  const blockGigMutation = useMutation({
    mutationFn: ({ gigId, reason }: { gigId: string; reason: string }) => adminApi.blockGig(gigId, { reason }),
    onSuccess: (updatedDetail) => {
      queryClient.setQueryData(["admin-gig-detail", updatedDetail.id], updatedDetail);
      void queryClient.invalidateQueries({ queryKey: ["admin-gigs"] });
      setModerationAction(null);
      setModerationReason("");
      setModerationError(null);
    },
  });

  const unblockGigMutation = useMutation({
    mutationFn: ({ gigId, reason }: { gigId: string; reason: string }) => adminApi.unblockGig(gigId, { reason }),
    onSuccess: (updatedDetail) => {
      queryClient.setQueryData(["admin-gig-detail", updatedDetail.id], updatedDetail);
      void queryClient.invalidateQueries({ queryKey: ["admin-gigs"] });
      setModerationAction(null);
      setModerationReason("");
      setModerationError(null);
    },
  });

  const moderationPending = blockGigMutation.isPending || unblockGigMutation.isPending;

  const closeModerationModal = () => {
    if (moderationPending) {
      return;
    }
    setModerationAction(null);
    setModerationReason("");
    setModerationError(null);
    blockGigMutation.reset();
    unblockGigMutation.reset();
  };

  const handleClose = () => {
    if (moderationPending) {
      return;
    }
    closeModerationModal();
    onClose();
  };

  const handleModerationSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!detail || moderationAction === null) {
      return;
    }
    const reason = moderationReason.trim();
    if (!reason) {
      setModerationError(`Enter a reason before ${moderationAction === "block" ? "blocking" : "unblocking"} this gig.`);
      return;
    }
    setModerationError(null);
    if (moderationAction === "block") {
      blockGigMutation.mutate({ gigId: detail.id, reason });
      return;
    }
    unblockGigMutation.mutate({ gigId: detail.id, reason });
  };

  const moderationMutationError = blockGigMutation.error
    ? apiErrorToMessage(blockGigMutation.error, "Failed to block this gig.")
    : unblockGigMutation.error
      ? apiErrorToMessage(unblockGigMutation.error, "Failed to unblock this gig.")
      : null;

  const isUnblockAction = moderationAction === "unblock";
  const moderationActionLabel = isUnblockAction ? "Unblock gig" : "Block gig";

  const openModerationModal = (action: ModerationAction) => {
    setModerationAction(action);
    setModerationReason("");
    setModerationError(null);
    blockGigMutation.reset();
    unblockGigMutation.reset();
  };

  const moderationSubmitLabel = moderationPending
    ? `${isUnblockAction ? "Unblocking" : "Blocking"}...`
    : moderationActionLabel;

  const moderationDescription = isUnblockAction
    ? "Unblocking this gig makes it eligible for public gig discovery again when its status allows it."
    : "Blocking this gig removes it from public gig discovery and stops new teen requests.";

  const moderationPlaceholder = isUnblockAction
    ? "Add the reason this gig is being unblocked"
    : "Add the reason this gig is being blocked";

  const moderationColorClasses = isUnblockAction
    ? "!border-emerald-200 !bg-emerald-600 !text-white hover:!bg-emerald-700 hover:!text-white"
    : "!border-rose-200 !bg-rose-600 !text-white hover:!bg-rose-700 hover:!text-white";

  const moderationNoticeClasses = isUnblockAction
    ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-4"
    : "rounded-2xl border border-rose-200 bg-rose-50 p-4";

  const moderationNoticeTextClasses = isUnblockAction
    ? "text-sm text-emerald-800"
    : "text-sm text-rose-800";

  const moderationReasonInputId = isUnblockAction ? "admin-unblock-gig-reason" : "admin-block-gig-reason";

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title={gig ? gig.title : "Gig detail"} panelClassName="max-w-4xl">
        {gigDetailQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading gig detail...</p> : null}
        {gigDetailQuery.error ? <p className="text-sm text-red-600">{apiErrorToMessage(gigDetailQuery.error, "Failed to load gig detail.")}</p> : null}

        {detail ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-myteengig-border p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <AdminPillBadge>{detail.status}</AdminPillBadge>
                  {detail.blocked ? <AdminPillBadge tone="danger">Blocked</AdminPillBadge> : null}
                </div>
              </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Compensation</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatGigCompensation(detail.paymentAmountCents, detail.volunteer)}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Duration</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatEstimatedDuration(detail.estimatedDurationMinutes)}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Requests</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.requestCount}</p>
            </div>
            </div>

            <div className={`rounded-2xl border p-4 ${detail.blocked ? "border-rose-200 bg-rose-50" : "border-myteengig-border"}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Block status</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    {detail.blocked
                      ? `Blocked${detail.blockedAt ? ` ${formatDateTime(detail.blockedAt)}` : ""}`
                      : "No block applied."}
                  </p>
                </div>
                {detail.blocked ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <AdminPillBadge tone="danger">Blocked</AdminPillBadge>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => openModerationModal("unblock")}
                    >
                      Unblock gig
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={() => openModerationModal("block")}
                  >
                    Block gig
                  </Button>
                )}
              </div>
              {detail.blocked ? (
                <p className="mt-3 whitespace-pre-wrap text-sm text-rose-800">
                  {detail.blockedReason ?? "No reason recorded."}
                </p>
              ) : null}
            </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Gig details</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{detail.category}</p>
                <p>{formatCityStateZip(detail)}</p>
                <p>Starts: {formatDateTimeInGigTimeZone(detail.startTime, detail.timeZone)}</p>
                <p>Ends: {formatDateTimeInGigTimeZone(detail.endTime, detail.timeZone)}</p>
                <p>Created: {formatDateTime(detail.createdAt)}</p>
                <p>Updated: {formatDateTime(detail.updatedAt)}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Poster</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{posterName}</p>
                <p>{detail.posterEmail ?? "No email"}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Description</p>
            <p className="mt-3 whitespace-pre-wrap text-sm text-myteengig-text-muted">{detail.description}</p>
          </div>

          {detail.requiresEquipment ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Equipment or supplies</p>
              <p className="mt-3 whitespace-pre-wrap text-sm text-myteengig-text-muted">
                {detail.equipmentDescription?.trim() || "Equipment is required, but details were not provided."}
              </p>
            </div>
          ) : null}

          <div className="rounded-2xl border border-myteengig-border p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-myteengig-text-primary">Requests</p>
              <AdminPillBadge>{detail.requestCount}</AdminPillBadge>
            </div>
            {detail.requests.length > 0 ? (
              <div className="mt-4 grid gap-3">
                {detail.requests.map((request) => (
                  <div
                    key={request.requestId}
                    className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-myteengig-text-primary">
                          {getRequesterDisplayName(request)}
                        </p>
                        <p className="mt-1 truncate text-sm text-myteengig-text-muted">
                          {request.requesterEmail ?? "No email provided"}
                        </p>
                        <p className="mt-2 text-xs text-myteengig-text-muted">
                          Applied: {formatDateTime(request.createdAt)}
                        </p>
                      </div>
                      <AdminPillBadge tone={getRequestTone(request.status)}>{request.status}</AdminPillBadge>
                    </div>

                    {request.requestMessage?.trim() ? (
                      <div className="mt-3 rounded-2xl border border-myteengig-border bg-white px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Application note</p>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-myteengig-text-primary">{request.requestMessage}</p>
                      </div>
                    ) : null}

                    <div className="mt-3 rounded-2xl border border-myteengig-border bg-white px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Completion</p>
                      {getCompletionItems(request, posterName).length > 0 ? (
                        <div className="mt-2 space-y-2">
                          {getCompletionItems(request, posterName).map((item) => (
                            <div key={`${request.requestId}-${item.label}`} className="flex items-start justify-between gap-4">
                              <span className="text-sm text-myteengig-text-muted">{item.label}</span>
                              <span className="text-right text-sm text-myteengig-text-primary">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-myteengig-text-muted">No completion actions recorded.</p>
                      )}
                    </div>

                    <div className="mt-3 rounded-2xl border border-myteengig-border bg-white px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Messages</p>
                        <AdminPillBadge>{request.messages.length}</AdminPillBadge>
                      </div>
                      {request.messages.length > 0 ? (
                        <div className="mt-3 space-y-3">
                          {request.messages.map((message) => (
                            <div key={message.id} className="rounded-2xl border border-myteengig-border bg-myteengig-background/40 px-4 py-3">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-myteengig-text-primary">
                                    {getMessageSenderLabel(message)}
                                  </p>
                                  <p className="mt-1 text-xs text-myteengig-text-muted">{formatDateTime(message.createdAt)}</p>
                                </div>
                              </div>
                              <p className="mt-3 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">{message.body}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-myteengig-text-muted">No message thread for this request.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No requests for this gig yet.</p>
            )}
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-myteengig-text-primary">Reviews</p>
              <AdminPillBadge>{detail.reviews.length}</AdminPillBadge>
            </div>
            {detail.reviews.length > 0 ? (
              <div className="mt-4 grid gap-3">
                {detail.reviews.map((review, index) => (
                  <div key={getReviewSummaryKey(review, index)} className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-myteengig-text-primary">
                          {getFullName(review.reviewerFirstName, review.reviewerLastName)} to {getFullName(review.revieweeFirstName, review.revieweeLastName)}
                        </p>
                        <p className="mt-1 text-xs text-myteengig-text-muted">
                          {getReviewContextLabel(review.reviewContext)} • {formatDateTime(review.createdAt)}
                        </p>
                      </div>
                      <AdminPillBadge>{review.rating.toFixed(1)} / 5</AdminPillBadge>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">
                      {review.message?.trim() ? review.message : "No written feedback was included with this review."}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No reviews tied to this gig yet.</p>
            )}
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Media</p>
            {detail.images.length > 0 ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {detail.images.map((image) => (
                  <a key={image.id} href={image.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border border-myteengig-border bg-myteengig-background">
                    <img src={image.url} alt={image.originalFileName ?? detail.title} className="h-40 w-full object-cover" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No media attached.</p>
            )}
          </div>
          </div>
        ) : null}
      </Modal>

      <Modal isOpen={isOpen && moderationAction !== null} onClose={closeModerationModal} title={moderationActionLabel} panelClassName="max-w-xl">
        <form className="space-y-5" onSubmit={handleModerationSubmit}>
          <div className={moderationNoticeClasses}>
            <p className={moderationNoticeTextClasses}>{moderationDescription}</p>
          </div>

          <div>
            <label htmlFor={moderationReasonInputId} className="mb-2 block text-sm font-semibold text-myteengig-text-primary">
              Reason
            </label>
            <textarea
              id={moderationReasonInputId}
              value={moderationReason}
              onChange={(event) => {
                setModerationReason(event.target.value);
                setModerationError(null);
              }}
              maxLength={MAX_MODERATION_REASON_LENGTH}
              rows={5}
              className="w-full rounded-lg border border-myteengig-border px-3 py-2 text-sm text-myteengig-text-primary shadow-xs focus:border-myteengig-primary focus:outline-none focus:ring-2 focus:ring-myteengig-primary/20"
              placeholder={moderationPlaceholder}
              required
            />
            <p className="mt-2 text-xs text-myteengig-text-muted">
              {moderationReason.length}/{MAX_MODERATION_REASON_LENGTH}
            </p>
          </div>

          {moderationError || moderationMutationError ? (
            <p className="text-sm font-medium text-red-600">{moderationError ?? moderationMutationError}</p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" onClick={closeModerationModal} disabled={moderationPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              className={moderationColorClasses}
              disabled={moderationPending}
            >
              {moderationSubmitLabel}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default AdminGigDetailModal;
